// Choosing a supplier or a customer in a form (docs/plans/add-here.md): one box
// to search by name or phone, with "Add … as new" as its last row, and the
// short form that row opens. The form behind is never left.
import type { CustomerRow, RowOf } from '@ims/data'
import { AnchoredList, Button, Field, Input, Modal, cx, useToast } from '@ims/ui'
import { AlertTriangle, Plus, Search, Truck, UserPlus, X } from '@ims/ui/icons'
import { useId, useRef, useState, type KeyboardEvent, type Ref } from 'react'
import { errorMessage, useLive } from './hooks.ts'
import { canOfferNew, findSameName, matchParties } from './lib/pick.ts'
import { insertOp, newCustomer, newSupplier } from './lib/rows.ts'
import { useCan, useRepo, useWorkspace } from './workspace.tsx'

type SupplierRow = RowOf<'suppliers'>

interface Party {
  id: string
  name: string
  phone: string | null
}

export function partyLabel(p: Pick<Party, 'name' | 'phone'>): string {
  return p.phone ? `${p.name} · ${p.phone}` : p.name
}

export interface PartyWords {
  /** "supplier" or "customer", as in "Add … as a new supplier". */
  one: string
  many: string
  placeholder: string
  /** The plus button and the last row before anything is typed. */
  newLabel: string
  /** The button that empties the box. */
  clearLabel: string
}

interface Option {
  key: string
  pick: () => void
  party?: Party
  /** The add row's words. */
  add?: string
}

/**
 * Type a name or phone, pick with ↑/↓ and Enter, or add a new one. A click
 * opens the whole list. Without `onAddNew` the box only chooses.
 */
export function PartyCombobox<T extends Party>({
  id,
  rows,
  value,
  onChange,
  onAddNew,
  words,
  inputRef,
  disabled,
  openOnClick,
  label,
}: {
  id?: string
  /** The box's name where no visible label points at it. */
  label?: string
  rows: readonly T[]
  value: T | null
  onChange: (party: T | null) => void
  /** Called with the typed text when the user asks to add a new one. */
  onAddNew?: (name: string) => void
  words: PartyWords
  inputRef?: Ref<HTMLInputElement>
  disabled?: boolean
  /** A click lists everyone (a box that stands in for a dropdown). */
  openOnClick?: boolean
}) {
  const listId = useId()
  const box = useRef<HTMLDivElement>(null)
  const [text, setText] = useState<string | null>(null)
  const [highlight, setHighlight] = useState(0)
  const open = text !== null
  const typed = (text ?? '').trim()
  const matches = open ? matchParties(rows, typed, typed ? 8 : 50) : []
  const addWords = !onAddNew
    ? null
    : typed
      ? canOfferNew(rows, typed)
        ? `Add “${typed}” as a new ${words.one}`
        : null
      : rows.length
        ? words.newLabel
        : `Add your first ${words.one}`
  const options: Option[] = [
    ...matches.map((p) => ({ key: p.id, party: p, pick: () => choose(p) })),
    ...(addWords
      ? [
          {
            key: 'new',
            add: addWords,
            pick: () => {
              setText(null)
              onAddNew?.(typed)
            },
          },
        ]
      : []),
  ]
  const index = Math.min(highlight, Math.max(options.length - 1, 0))
  // Never an empty list: say why nothing is offered.
  const note = !open
    ? null
    : rows.length === 0
      ? `No ${words.many} yet.`
      : matches.length === 0
        ? `No ${words.one} matches “${typed}”.${onAddNew ? '' : ' Ask a manager to add it.'}`
        : null

  function choose(p: T | null) {
    setText(null)
    onChange(p)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) setText('')
      setHighlight(options.length ? (index + 1) % options.length : 0)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(options.length ? (index - 1 + options.length) % options.length : 0)
    } else if (e.key === 'Enter' && open) {
      e.preventDefault()
      options[index]?.pick()
    } else if (e.key === 'Escape' && open) {
      e.preventDefault()
      e.stopPropagation()
      setText(null)
    }
  }

  return (
    <div>
      <div ref={box} className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-subtle"
          aria-hidden
        />
        <input
          ref={inputRef}
          id={id}
          aria-label={label}
          role="combobox"
          aria-expanded={open && options.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && options.length ? `${listId}-${index}` : undefined}
          autoComplete="off"
          disabled={disabled}
          value={open ? (text ?? '') : value ? partyLabel(value) : ''}
          placeholder={words.placeholder}
          onFocus={(e) => e.target.select()}
          onClick={() => {
            if (openOnClick && !open) setText('')
          }}
          onChange={(e) => {
            setText(e.target.value)
            setHighlight(0)
          }}
          onBlur={() => setText(null)}
          onKeyDown={onKeyDown}
          className={cx(
            'w-full rounded-xl border border-border bg-surface py-2 pl-9 text-sm placeholder:text-subtle focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-soft disabled:text-muted',
            value || onAddNew ? 'pr-[4.5rem]' : 'pr-3',
          )}
        />
        {!disabled && (
          <div className="absolute inset-y-0 right-1.5 flex items-center gap-1">
            {value && (
              <button
                type="button"
                aria-label={words.clearLabel}
                title={words.clearLabel}
                onClick={() => choose(null)}
                className="tap-target rounded-lg p-1 text-muted hover:bg-soft hover:text-ink"
              >
                <X className="size-4" />
              </button>
            )}
            {onAddNew && (
              <button
                type="button"
                aria-label={words.newLabel}
                title={words.newLabel}
                onClick={() => onAddNew('')}
                className="tap-target flex size-7 items-center justify-center rounded-lg bg-primary-light text-primary hover:bg-primary/20"
              >
                <Plus className="size-4" />
              </button>
            )}
          </div>
        )}
      </div>
      {open && (options.length > 0 || note) && (
        <AnchoredList anchor={box} maxHeight={288}>
          {note && <p className="px-3 py-2 text-sm text-muted">{note}</p>}
          <ul id={listId} role="listbox">
            {options.map((o, i) => (
              <li
                key={o.key}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === index}
                aria-label={o.party ? partyLabel(o.party) : o.add}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlight(i)}
                onClick={o.pick}
                className={cx(
                  'flex min-h-10 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm',
                  o.add ? 'font-semibold text-primary' : i === index ? 'bg-soft' : undefined,
                  o.add && i === index && 'bg-primary-light',
                  // The add row stands apart from the names above it.
                  o.add && (matches.length > 0 || note) && 'mt-1 border-t border-border pt-2',
                )}
              >
                {o.party ? (
                  <span className="min-w-0 truncate">
                    <span className="font-semibold">{o.party.name}</span>
                    {o.party.phone && (
                      <span className="ml-2 text-xs text-muted">{o.party.phone}</span>
                    )}
                  </span>
                ) : (
                  <>
                    <span
                      aria-hidden
                      className="flex size-6 shrink-0 items-center justify-center rounded-lg bg-primary/15"
                    >
                      <Plus className="size-3.5" />
                    </span>
                    <span className="min-w-0 truncate">{o.add}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </AnchoredList>
      )}
    </div>
  )
}

const WORDS = {
  suppliers: {
    title: 'New supplier',
    about: 'Name and phone now. The rest later, on the supplier’s page.',
    save: 'Add supplier',
    done: 'Supplier added',
  },
  customers: {
    title: 'New customer',
    about: 'Name and phone now. The rest later, on the customer’s page.',
    save: 'Add customer',
    done: 'Customer added',
  },
} as const

type PartyRow<K extends 'suppliers' | 'customers'> = K extends 'suppliers'
  ? SupplierRow
  : CustomerRow

/**
 * The short form: a name and a phone now, the rest later on the party's own
 * page. A name the business already has is pointed out before a second is made.
 */
export function NewPartyDialog<K extends 'suppliers' | 'customers'>({
  kind,
  initialName,
  onClose,
  onCreated,
}: {
  kind: K
  initialName: string
  onClose: () => void
  /** The row that was made, or the existing one the user chose to use instead. */
  onCreated: (party: PartyRow<K>) => void
}) {
  const repo = useRepo()
  const toast = useToast()
  const { current } = useWorkspace()
  const words = WORDS[kind]
  const [name, setName] = useState(initialName)
  const [phone, setPhone] = useState('')
  const [pan, setPan] = useState('')
  const [busy, setBusy] = useState(false)
  const existing =
    useLive(
      async (r): Promise<(SupplierRow | CustomerRow)[]> => {
        const active = { where: { is_active: true }, limit: 5000 } as const
        return kind === 'suppliers'
          ? (await r.list('suppliers', active)).rows
          : (await r.list('customers', active)).rows
      },
      [kind],
      [kind],
    ).data ?? []
  // Not while saving: the row just made must not be warned about.
  const same = busy ? null : (findSameName(existing, name) as PartyRow<K> | null)

  const save = async () => {
    if (!current || !name.trim()) return toast.error('Name is required')
    setBusy(true)
    try {
      const ctx = { tenantId: current.tenant_id }
      const fields = { name: name.trim(), phone: phone.trim() || null }
      const row =
        kind === 'suppliers'
          ? newSupplier(ctx, { ...fields, pan_vat_number: pan.trim() || null })
          : newCustomer(ctx, fields)
      await repo.commit([
        kind === 'suppliers'
          ? insertOp('suppliers', row as ReturnType<typeof newSupplier>)
          : insertOp('customers', row as ReturnType<typeof newCustomer>),
      ])
      toast.success(words.done)
      onCreated(row as PartyRow<K>)
    } catch (error) {
      toast.error(errorMessage(error))
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      area={kind === 'suppliers' ? 'buy' : 'people'}
      icon={kind === 'suppliers' ? <Truck className="size-5" /> : <UserPlus className="size-5" />}
      title={words.title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => void save()}>
            {same ? 'Add anyway' : words.save}
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        <p className="text-xs text-muted">{words.about}</p>
        <Field label="Name">
          {(id) => (
            <Input
              id={id}
              value={name}
              maxLength={120}
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        {same && (
          <p
            role="status"
            className="flex items-start gap-2 rounded-xl bg-warning-light px-3 py-2 text-sm text-warning"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              You already have <b translate="no">{same.name}</b>.{' '}
              <button type="button" className="font-bold underline" onClick={() => onCreated(same)}>
                Use it instead
              </button>
            </span>
          </p>
        )}
        <Field label="Phone (optional)">
          {(id) => (
            <Input
              id={id}
              type="tel"
              inputMode="tel"
              maxLength={40}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          )}
        </Field>
        {kind === 'suppliers' && (
          <Field label="PAN or VAT no. (optional)">
            {(id) => (
              <Input id={id} maxLength={20} value={pan} onChange={(e) => setPan(e.target.value)} />
            )}
          </Field>
        )}
        <button type="submit" hidden />
      </form>
    </Modal>
  )
}

/**
 * The Supplier field of a form: search the active suppliers, or add one in place
 * (staff and up, like the Suppliers page). `value` is the supplier's id, '' for none.
 */
export function SupplierPicker({
  id,
  value,
  onChange,
  optional,
  disabled,
  label,
}: {
  id?: string
  label?: string
  value: string
  onChange: (supplierId: string) => void
  /** The form may be saved without a supplier. */
  optional?: boolean
  disabled?: boolean
}) {
  const canAdd = useCan('staff')
  const [adding, setAdding] = useState<string | null>(null)
  // The one just added, until the live list has it.
  const [made, setMade] = useState<SupplierRow | null>(null)
  const rows =
    useLive(
      (r) => r.list('suppliers', { orderBy: { column: 'name' }, limit: 2000 }),
      [],
      ['suppliers'],
    ).data?.rows ?? []
  // An inactive supplier already on the document stays shown, but is not offered.
  const chosen = rows.find((s) => s.id === value) ?? (made?.id === value ? made : null)
  return (
    <>
      <PartyCombobox
        id={id}
        rows={rows.filter((s) => s.is_active)}
        value={chosen}
        onChange={(s) => onChange(s?.id ?? '')}
        onAddNew={canAdd && !disabled ? setAdding : undefined}
        disabled={disabled}
        label={label}
        openOnClick
        words={{
          one: 'supplier',
          many: 'suppliers',
          placeholder: optional ? 'No supplier · search name or phone' : 'Search suppliers…',
          newLabel: 'New supplier',
          clearLabel: 'No supplier',
        }}
      />
      {adding !== null && (
        <NewPartyDialog
          kind="suppliers"
          initialName={adding}
          onClose={() => setAdding(null)}
          onCreated={(s) => {
            setAdding(null)
            setMade(s)
            onChange(s.id)
          }}
        />
      )}
    </>
  )
}
